/**
 * React Context for venue auth state.
 * Provides currentVenue, isLoggedIn, login, logout across the entire app.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api';
import { analytics } from './analytics';

interface Venue {
  id: string;
  name: string;
  email: string;
  address: string;
  phone: string;
  logoUrl: string | null;
}

export interface OnboardData {
  email: string;
  password: string;
  name: string;
  address: string;
  phone: string;
  menuItems: { name: string; category: string; pricePaise: number }[];
  programName: string;
  stampsRequired: number;
  rewardDescription: string;
  rewardExpiryDays: number;
}

interface AuthContextType {
  currentVenue: Venue | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, address: string, phone: string) => Promise<void>;
  onboard: (data: OnboardData) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentVenue, setCurrentVenue] = useState<Venue | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if token exists and fetch current venue on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('venue_token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      api.setToken(token);
      const result = await api.getMe();

      if (result.success && result.data) {
        const venue = result.data as Venue;
        setCurrentVenue(venue);
        analytics.identify(venue.id, venue.name);
      } else {
        api.clearToken();
      }

      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const result = await api.login(email, password);
    if (result.success && result.data) {
      api.setToken(result.data.token);
      const meResult = await api.getMe();
      if (meResult.success && meResult.data) {
        const venue = meResult.data as Venue;
        setCurrentVenue(venue);
        analytics.identify(venue.id, venue.name);
        analytics.venueLoggedIn(venue.id, venue.name);
      }
    } else {
      throw new Error(result.error || 'Login failed');
    }
  };

  const register = async (email: string, password: string, name: string, address: string, phone: string) => {
    const result = await api.register(email, password, name, address, phone);
    if (result.success && result.data) {
      api.setToken(result.data.token);
      const meResult = await api.getMe();
      if (meResult.success && meResult.data) {
        const venue = meResult.data as Venue;
        setCurrentVenue(venue);
        analytics.identify(venue.id, venue.name);
        analytics.venueRegistered(venue.id, venue.name);
      }
    } else {
      throw new Error(result.error || 'Registration failed');
    }
  };

  const onboard = async (data: OnboardData) => {
    const result = await api.onboard(data);
    if (result.success && result.data) {
      api.setToken(result.data.token);
      const meResult = await api.getMe();
      if (meResult.success && meResult.data) {
        const venue = meResult.data as Venue;
        setCurrentVenue(venue);
        analytics.identify(venue.id, venue.name);
        analytics.venueRegistered(venue.id, venue.name);
      }
    } else {
      throw new Error(result.error || 'Onboarding failed');
    }
  };

  const logout = () => {
    analytics.venueLoggedOut();
    analytics.reset();
    api.clearToken();
    setCurrentVenue(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentVenue,
        isLoading,
        isLoggedIn: !!currentVenue,
        login,
        register,
        onboard,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
